/**
 * ui-sfx — tiny synthesizer for sound feedback in the toy shell.
 *
 * The main audio engine (game-core) loads instrument SAMPLES; the shell only
 * needs UI "pops" and "boings". Synthesizing them with oscillators costs 0
 * assets, 0 network (offline-first), and <1ms of CPU. Its own lazy context is
 * unlocked by the gesture that triggers the sound (pops are ONLY called from
 * touch handlers, so the autoplay policy is automatically satisfied). In
 * environments without WebAudio (jsdom/tests), everything is a silent no-op.
 */

let ctx: AudioContext | null = null;

function ensureContext(): AudioContext | null {
  // Widening to `| undefined`: jsdom/tests lack WebAudio (silent no-op).
  const Ctor = globalThis.AudioContext as typeof AudioContext | undefined;
  if (!Ctor) {
    return null;
  }
  ctx ??= new Ctor();
  if (ctx.state === "suspended") {
    void ctx.resume();
  }
  return ctx;
}

/** Major pentatonic scale — any combination of pops sounds "good". */
const PENTATONIC_SEMITONES = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21] as const;

/**
 * Playful tap pop: a short blip with an upward tone slide.
 * `step` selects the note (pentatonic)—each home object has its own voice.
 * `volume` is in [0,1]; at 0, nothing plays (parent control).
 */
export function playTapPop(volume: number, step = 0): void {
  if (volume <= 0) {
    return;
  }
  const audio = ensureContext();
  if (!audio) {
    return;
  }
  const now = audio.currentTime;
  const semitone = PENTATONIC_SEMITONES[Math.abs(step) % PENTATONIC_SEMITONES.length] ?? 0;
  const base = 392 * 2 ** (semitone / 12); // G4 as warm root

  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(base, now);
  // Small upward slide: the toy's "boing!" signature.
  osc.frequency.exponentialRampToValueAtTime(base * 1.6, now + 0.09);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.35 * volume, now + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  osc.connect(gain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + 0.18);
}

/**
 * Lower-pitched bounce "boing" (landings, closures). Same contract as
 * `playTapPop`.
 */
export function playBoing(volume: number): void {
  if (volume <= 0) {
    return;
  }
  const audio = ensureContext();
  if (!audio) {
    return;
  }
  const now = audio.currentTime;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(220, now);
  osc.frequency.exponentialRampToValueAtTime(120, now + 0.18);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.3 * volume, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
  osc.connect(gain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + 0.26);
}
